import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { abrirTarefaDaDefesa } from '@/hooks/useTarefas';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { usePedidoMovement } from '@/hooks/usePedidoMovement';
import { depoisDoClique, manterNaTriagem, marcarTelasDeForaDesatualizadas, meuUsuarioId } from '@/components/kanban/triagem/acaoTriagem';

// O erro do supabase-js é um objeto com `message`, não um Error: sem isto o toast
// dizia só "Tente novamente" e escondia o motivo real da recusa do banco.
const mensagemDoErro = (error: unknown): string => {
  const mensagem = typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : '';
  return mensagem.trim() || 'Tente novamente.';
};

export type ColunaDefesaCredito = 'ANALISE MANUAL CREDITO' | 'CREDITO REPROVADO';

interface ConfigDefesaCredito {
  titulo: string;
  fila: string;
  queryKey: string;
}

export const TRIAGENS_DEFESA_CREDITO: Record<ColunaDefesaCredito, ConfigDefesaCredito> = {
  'ANALISE MANUAL CREDITO': {
    titulo: 'Triagem Análise Manual Crédito',
    fila: 'Análise Manual Crédito',
    queryKey: 'triagem-analise-manual-credito',
  },
  'CREDITO REPROVADO': {
    titulo: 'Triagem Crédito Reprovado',
    fila: 'Crédito Reprovado',
    queryKey: 'triagem-credito-reprovado',
  },
};

export interface DefesaCredito {
  id: string;
  numero_defesa: string;
  data_defesa: string;
  created_at: string;
  status: string;
  observacao: string | null;
  motivo_reprovacao: string | null;
  tentativa_id: string | null;
  codigos_cr?: string[] | null;
  retorno?: string | null;
  retorno_codigos?: string[] | null;
  retorno_em?: string | null;
}

export interface ItemTriagemDefesaCredito {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  uf: string;
  produto: string;
  numero_cotacao_operadora: string;
  linha: string;
  quantidade: number;
  plano_valor: number;
  subtotal: number;
  entrada_coluna_em: string;
  status_id: string;
  coluna_atual: string;
  quadro_atual: string;
  cliente_id: string;
  is_first_of_cnpj: boolean;
  defesas: DefesaCredito[];
  credito_motivo_reprovacao: string | null;
  print_credito_path: string | null;
  ultima_defesa_created_at: string | null;
  ultima_defesa_status: string | null;
  sla_defesa_horas: number | null;
  sla_defesa_vencido: boolean;
}

export interface ResumoCotacoes {
  total: number;
  com_defesa: number;
  sem_defesa: number;
  sla_vencido: number;
}

export function useTriagemDefesaCredito(coluna: ColunaDefesaCredito) {
  const config = TRIAGENS_DEFESA_CREDITO[coluna];
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useUserProfileContext();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const { data: itens = [], isLoading, refetch, error: erroConsulta } = useQuery({
    queryKey: [config.queryKey],
    queryFn: async () => {
      // 1. Buscar itens da v_planilha_mae
      const lider = await equipesDoLider(profile);
      const { data, error } = await filtrarPeloLider(supabase
        .from('v_planilha_mae')
        .select('item_id, pedido_id, numero_pedido, cnpj, razao_social, uf, produto, numero_cotacao_operadora, linha, ddd, quantidade, plano_valor, subtotal, valor_unit, entrada_coluna_em, status_id, coluna_atual, quadro_atual, cliente_id, manter_em, tipo_pessoa, documento')
        .eq('coluna_atual', coluna)
        .eq('quadro_atual', 'TRAMITACAO_PRIME')
        .order('manter_em', { ascending: true, nullsFirst: true })
        .order('cnpj', { ascending: true })
        .order('pedido_id', { ascending: true })
        .order('entrada_coluna_em', { ascending: true }), profile, lider);

      if (error) { onQueryError(error); throw error; }
      onQuerySuccess();

      const items = (data || []) as any[];
      if (items.length === 0) return [];

      // 2. Buscar UF do leads_carteira por CNPJ
      const cnpjs = [...new Set(items.map(i => i.cnpj).filter(Boolean))];
      const ufByCnpj: Record<string, string> = {};
      if (cnpjs.length > 0) {
        const { data: ufs } = await supabase
          .from('leads_carteira')
          .select('cnpj, uf')
          .in('cnpj', cnpjs)
          .not('uf', 'is', null)
          .neq('uf', '');
        (ufs || []).forEach((row: any) => {
          if (row.cnpj && row.uf) ufByCnpj[row.cnpj] = row.uf;
        });
      }

      // 3. Buscar defesas de todos os itens
      const itemIds = items.map(i => i.item_id);
      const { data: defesas, error: erroDefesas } = await supabase
        .from('defesas_credito')
        .select('id, item_id, numero_defesa, data_defesa, created_at, status, observacao, motivo_reprovacao, tentativa_id, codigos_cr, retorno, retorno_codigos, retorno_em')
        .in('item_id', itemIds)
        .order('data_defesa', { ascending: true });
      if (erroDefesas) throw erroDefesas;
      const { data: motivos, error: erroMotivos } = await supabase.from('itens_pedido')
        .select('id, credito_motivo_reprovacao').in('id', itemIds);
      if (erroMotivos) throw erroMotivos;
      const motivosByItem = new Map((motivos || []).map(m => [m.id, m.credito_motivo_reprovacao]));

      const defesasByItem: Record<string, DefesaCredito[]> = {};
      (defesas || []).forEach((d: any) => {
        if (!defesasByItem[d.item_id]) defesasByItem[d.item_id] = [];
        defesasByItem[d.item_id].push({
          id: d.id,
          numero_defesa: d.numero_defesa,
          data_defesa: d.data_defesa,
          created_at: d.created_at,
          status: d.status,
          observacao: d.observacao,
          motivo_reprovacao: d.motivo_reprovacao,
          tentativa_id: d.tentativa_id,
          codigos_cr: d.codigos_cr ?? null,
          retorno: d.retorno ?? null,
          retorno_codigos: d.retorno_codigos ?? null,
          retorno_em: d.retorno_em ?? null,
        });
      });

      // 4. Buscar anexos PRINT_CREDITO dos pedidos
      const pedidoIds = [...new Set(items.map(i => i.pedido_id))];
      const { data: anexos } = await supabase
        .from('anexos_crm')
        .select('pedido_id, path')
        .in('pedido_id', pedidoIds)
        .eq('tipo_anexo', 'PRINT_CREDITO');

      const printByPedido: Record<string, string> = {};
      (anexos || []).forEach((a: any) => {
        printByPedido[a.pedido_id] = a.path;
      });

      const now = Date.now();
      let lastCnpj = '';
      const processed: ItemTriagemDefesaCredito[] = items.map((item: any) => {
        const isFirst = item.cnpj !== lastCnpj;
        lastCnpj = item.cnpj || '';
        const itemDefesas = defesasByItem[item.item_id] || [];

        // Find the most recent PENDENTE defense for SLA
        const defesasPendentes = itemDefesas.filter(d => d.status === 'PENDENTE');
        const ultimaPendente = defesasPendentes.length > 0
          ? defesasPendentes.reduce((latest, d) =>
              new Date(d.created_at).getTime() > new Date(latest.created_at).getTime() ? d : latest
            )
          : null;

        const slaHoras = ultimaPendente
          ? (now - new Date(ultimaPendente.created_at).getTime()) / (1000 * 60 * 60)
          : null;

        return {
          item_id: item.item_id,
          pedido_id: item.pedido_id,
          numero_pedido: String(item.numero_pedido || ''),
          cnpj: item.cnpj || '',
          tipo_pessoa: item.tipo_pessoa ?? null,
          documento: item.documento || item.cnpj || '',
          razao_social: item.razao_social || '',
          uf: ufByCnpj[item.cnpj] || item.uf || '',
          produto: item.produto || '',
          numero_cotacao_operadora: item.numero_cotacao_operadora || '',
          linha: `${item.ddd || ''}${item.linha || ''}`,
          quantidade: item.quantidade || 0,
          plano_valor: item.plano_valor || item.valor_unit || 0,
          subtotal: Number(item.subtotal) || (Number(item.plano_valor || item.valor_unit) * Number(item.quantidade || 1)),
          entrada_coluna_em: item.entrada_coluna_em || '',
          status_id: item.status_id || '',
          coluna_atual: item.coluna_atual || coluna,
          quadro_atual: item.quadro_atual || 'TRAMITACAO_PRIME',
          cliente_id: item.cliente_id || '',
          is_first_of_cnpj: isFirst,
          defesas: itemDefesas,
          credito_motivo_reprovacao: motivosByItem.get(item.item_id) || null,
          print_credito_path: printByPedido[item.pedido_id] || null,
          ultima_defesa_created_at: ultimaPendente?.created_at || null,
          ultima_defesa_status: ultimaPendente?.status || (itemDefesas.length > 0 ? itemDefesas[itemDefesas.length - 1].status : null),
          sla_defesa_horas: slaHoras,
          // A Claro avalia em até 24h: passou disso sem retorno, a tentativa venceu
          // e a cotação precisa de motivo e defesa novos.
          sla_defesa_vencido: slaHoras !== null && slaHoras > 24,
        };
      });
      return processed;
    },
    refetchInterval,
  });

  const { moverPedidoJunto } = usePedidoMovement();

  // O que fica na tela (defesa, motivo, cotação): relê só a triagem; o Kanban e
  // a Planilha ficam marcados e se leem quando aparecem.
  const releFila = () => {
    void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
    void queryClient.invalidateQueries({ queryKey: ['crm-status-pedidos'] });
    marcarTelasDeForaDesatualizadas(queryClient);
  };

  // O que tira a cotação da fila (ou a manda para o fim): a linha anda na tela
  // assim que o banco confirma.
  const depoisDeAndar = (pedidoId: string, itensIds: string[], modo: 'sai' | 'desce') => {
    void queryClient.invalidateQueries({ queryKey: ['crm-status-pedidos'] });
    return depoisDoClique(queryClient, [config.queryKey], { pedidoId, itens: itensIds }, modo);
  };

  // Helper: buscar todos os item_ids da mesma cotação
  const getItemIdsByCotacao = (itemId: string): ItemTriagemDefesaCredito[] => {
    const alvo = itens.find(i => i.item_id === itemId);
    if (!alvo) return [];
    return itens.filter(i => i.pedido_id === alvo.pedido_id && i.cnpj === alvo.cnpj
      && (alvo.numero_cotacao_operadora ? i.numero_cotacao_operadora === alvo.numero_cotacao_operadora : i.item_id === itemId));
  };

  // Registrar nova defesa (para TODOS os itens da mesma cotação)
  const registrarDefesa = useMutation({
    mutationFn: async ({ itemId, numeroDefesa, motivo, defesa, codigos }: { itemId: string; numeroDefesa: string; motivo: string; defesa: string; codigos?: string[]; cnpj?: string }) => {
      if (![numeroDefesa, motivo, defesa].every(texto => texto.trim())) throw new Error('Preencha número, motivo da Claro e defesa da Prime.');
      const alvo = itens.find(i => i.item_id === itemId);
      if (!alvo) throw new Error('Item não encontrado');
      const itensCotacao = itens.filter(i => i.pedido_id === alvo.pedido_id && i.cnpj === alvo.cnpj
        && (alvo.numero_cotacao_operadora ? i.numero_cotacao_operadora === alvo.numero_cotacao_operadora : i.item_id === itemId));
      if (itensCotacao.length === 0) throw new Error('Nenhum item encontrado para esta cotação');

      const usuarioId = await meuUsuarioId();

      // Um único INSERT: ou todos os itens recebem a tentativa completa, ou nenhum.
      const tentativaId = crypto.randomUUID();
      const { error } = await supabase.from('defesas_credito').insert(itensCotacao.map(item => ({
            tentativa_id: tentativaId,
            item_id: item.item_id,
            pedido_id: item.pedido_id,
            numero_defesa: numeroDefesa.trim(),
            motivo_reprovacao: motivo.trim(),
            observacao: defesa.trim(),
            codigos_cr: codigos?.length ? codigos : null,
            usuario_id: usuarioId,
            status: 'PENDENTE',
          })));
      if (error) throw error;
      const tarefa = await abrirTarefaDaDefesa(tentativaId);
      return { count: itensCotacao.length, tarefa };
    },
    onSuccess: ({ count, tarefa }) => {
      toast({ title: 'Defesa registrada', description: `Motivo e defesa salvos em ${count} ite${count !== 1 ? 'ns' : 'm'} do pedido.${tarefa.ok ? ' O consultor recebeu a tarefa.' : ''}` });
      if (!tarefa.ok) toast({ title: 'A tarefa do consultor não foi aberta', description: tarefa.erro, variant: 'destructive' });
      releFila();
    },
    onError: (error) => {
      toast({ title: 'Erro ao registrar defesa', description: mensagemDoErro(error), variant: 'destructive' });
    },
  });

  // Aprovado CPC → mover TODOS da mesma cotação para INSPEÇÃO CONCLUÍDA, numa
  // ida (fn_mover_pedido_junto grava o historico_status de cada linha)
  const aprovarCPC = useMutation({
    mutationFn: async ({ itemId, cnpj }: { itemId: string; cnpj?: string }) => {
      const itensCotacao = getItemIdsByCotacao(itemId);
      if (itensCotacao.length === 0) throw new Error('Nenhum item encontrado para esta cotação');
      const pedidoId = itensCotacao[0].pedido_id;
      const motivo = 'Aprovado CPC - Defesa de crédito aceita';

      const r = await moverPedidoJunto({
        pedidoId,
        itensIds: itensCotacao.map(i => i.item_id),
        quadroOrigem: 'TRAMITACAO_PRIME',
        colunaOrigem: coluna,
        toQuadro: 'PRE_ATIVO' as Parameters<typeof moverPedidoJunto>[0]['toQuadro'],
        toColuna: 'INSPEÇÃO CONCLUIDA',
        motivo,
        origemTriagem: true,
      });
      const movidos = (r.movidos ?? []).map(m => m.item_id);

      // O registro do movimento continua um por linha aprovada, num insert só.
      const usuarioId = await meuUsuarioId();
      const { error: erroMovimento } = await supabase.from('movimento').insert(movidos.map(() => ({
        entidade: 'pedido', entidade_id: pedidoId, tipo: 'status', motivo,
        icone: 'check-circle', cor: 'green', usuario_id: usuarioId, cnpj: cnpj || null,
      })) as never);
      if (erroMovimento) console.error('Aprovado CPC: movimento não registrado', erroMovimento);

      return { pedidoId, movidos };
    },
    onSuccess: ({ pedidoId, movidos }) => {
      const count = movidos.length;
      toast({ title: 'Aprovado CPC', description: `Defesa aceita. ${count} ite${count !== 1 ? 'ns' : 'm'} movido${count !== 1 ? 's' : ''} para Inspeção Concluída.` });
      void depoisDeAndar(pedidoId, movidos, 'sai');
    },
    onError: (error) => {
      toast({ title: 'Erro ao aprovar', description: mensagemDoErro(error), variant: 'destructive' });
      void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
    },
  });

  // Manter no final da fila (TODOS da mesma cotação), numa ida: fn_triagem_manter
  // grava o movimento e o manter_em só das linhas que ainda estão nesta fila
  const moverParaFinal = useMutation({
    mutationFn: async ({ itemId }: { itemId: string; cnpj?: string }) => {
      const itensCotacao = getItemIdsByCotacao(itemId);
      if (itensCotacao.length === 0) throw new Error('Nenhum item encontrado para esta cotação');
      const pedidoId = itensCotacao[0].pedido_id;

      const r = await manterNaTriagem({
        pedidoId,
        itens: itensCotacao.map(i => i.item_id),
        coluna,
        movimento: `Mantido na fila de ${config.fila}`,
      });
      return { pedidoId, movidos: r.itens ?? [] };
    },
    onSuccess: ({ pedidoId, movidos }) => {
      const count = movidos.length;
      toast({ title: 'Manter', description: `${count} ite${count !== 1 ? 'ns' : 'm'} movido${count !== 1 ? 's' : ''} para o final da fila.` });
      void depoisDeAndar(pedidoId, movidos, 'desce');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: mensagemDoErro(error), variant: 'destructive' });
      void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
    },
  });

  // Download Print Crédito
  const downloadPrintCredito = async (path: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('anexos-crm')
        .download(path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = path.split('/').pop() || 'print-credito';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: 'Erro ao baixar', description: 'Não foi possível baixar o Print Crédito.', variant: 'destructive' });
    }
  };

  // Correção explícita de UMA tentativa, nunca de todas as pendentes da cotação.
  const regularizarCredito = useMutation({
    mutationFn: async ({ itemId, defesaIds, motivo, defesa, cotacao }: { itemId: string; defesaIds?: string[]; motivo: string; defesa?: string; cotacao?: string }) => {
      if (!motivo.trim() || (defesaIds && (!defesaIds.length || !defesa?.trim()))) throw new Error('Preencha os campos obrigatórios.');
      const alvo = itens.find(i => i.item_id === itemId);
      if (!alvo) throw new Error('Item não encontrado; atualize a triagem.');
      // Legado entrou sem cotação e por isso nunca pôde ser defendido. O número é
      // completado aqui, no mesmo salvamento do motivo — sem sair da triagem.
      const faltaCotacao = !alvo.numero_cotacao_operadora.trim();
      if (faltaCotacao && !cotacao?.trim()) throw new Error('Informe o número da cotação da operadora.');
      const itemIds = (faltaCotacao
        ? itens.filter(i => i.pedido_id === alvo.pedido_id && i.cnpj === alvo.cnpj && !i.numero_cotacao_operadora.trim())
        : getItemIdsByCotacao(itemId)).map(i => i.item_id);
      if (faltaCotacao && !defesaIds) {
        const { error } = await supabase.from('itens_pedido')
          .update({ numero_cotacao_operadora: cotacao!.trim() }).in('id', itemIds).eq('coluna_atual', coluna);
        if (error) throw error;
      }
      const resultado = defesaIds
        ? await supabase.from('defesas_credito').update({ motivo_reprovacao: motivo.trim(), observacao: defesa!.trim() }).in('id', defesaIds).in('item_id', itemIds).select('id')
        : await supabase.from('itens_pedido').update({ credito_motivo_reprovacao: motivo.trim() }).in('id', itemIds).eq('coluna_atual', coluna).select('id');
      if (resultado.error) throw resultado.error;
      if (!resultado.data?.length) throw new Error('Registro mudou ou não está disponível; atualize a triagem.');
    },
    onSuccess: () => { releFila(); toast({ title: 'Dados de crédito atualizados' }); },
    onError: error => toast({ title: 'Não foi possível salvar', description: error instanceof Error ? error.message : 'Confira os dados.', variant: 'destructive' }),
  });

  // Compute resumoCotacoes from itens
  const resumoCotacoes: ResumoCotacoes = (() => {
    const cotacaoMap = new Map<string, ItemTriagemDefesaCredito[]>();
    itens.forEach(item => {
      const key = item.numero_cotacao_operadora || item.item_id;
      if (!cotacaoMap.has(key)) cotacaoMap.set(key, []);
      cotacaoMap.get(key)!.push(item);
    });

    let comDefesa = 0;
    let semDefesa = 0;
    let slaVencido = 0;

    cotacaoMap.forEach((items) => {
      const hasDefesa = items.some(i => i.defesas.length > 0);
      if (hasDefesa) {
        comDefesa++;
        // Check if any item in this cotação has a vencido SLA
        if (items.some(i => i.sla_defesa_vencido)) slaVencido++;
      } else {
        semDefesa++;
      }
    });

    return {
      total: cotacaoMap.size,
      com_defesa: comDefesa,
      sem_defesa: semDefesa,
      sla_vencido: slaVencido,
    };
  })();

  return {
    itens,
    erroConsulta,
    resumoCotacoes,
    isLoading,
    refetch,
    registrarDefesa: registrarDefesa.mutate,
    registrarDefesaAsync: registrarDefesa.mutateAsync,
    isRegistrandoDefesa: registrarDefesa.isPending,
    aprovarCPC: aprovarCPC.mutateAsync,
    moverParaFinal: moverParaFinal.mutateAsync,
    downloadPrintCredito,
    regularizarCreditoAsync: regularizarCredito.mutateAsync,
    isRegularizandoCredito: regularizarCredito.isPending,
  };
}
